import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DiscountType, PromoCode } from "@/core/entities/promo-code";
import { ProductType } from "@/core/entities/product";

interface PromoCodeViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promoCode: PromoCode | null;
}

export function PromoCodeViewDialog({
  open,
  onOpenChange,
  promoCode,
}: Readonly<PromoCodeViewDialogProps>) {
  const getEnabledForBadge = (enabledFor: ProductType) => {
    switch (enabledFor) {
      case ProductType.SELL:
        return (
          <Badge className="bg-muted text-secondary-foreground hover:bg-muted">
            Sell
          </Badge>
        );
      case ProductType.BUY:
        return (
          <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary">
            Buy
          </Badge>
        );
      case ProductType.BOTH:
        return (
          <Badge className="bg-primary/10 text-primary hover:bg-primary/10">
            Both
          </Badge>
        );
      default:
        return null;
    }
  };

  const formatDiscount = (promoCode: PromoCode) => {
    if (promoCode.discount.type === DiscountType.PERCENTAGE) {
      return `${promoCode.discount.value}%`;
    }
    return `$${promoCode.discount.value.toFixed(2)}`;
  };

  const isExpired = (promoCode: PromoCode) => {
    if (!promoCode.expirationDate) return false;
    return new Date(promoCode.expirationDate) < new Date();
  };

  if (!promoCode) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-2xl max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
      >
        <SheetHeader className="p-0">
          <SheetTitle>Promo Code Details</SheetTitle>
          <SheetDescription>
            Comprehensive view of the promo code information
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="pb-2">Name</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {promoCode.name}
              </p>
            </div>

            <div>
              <Label className="pb-2">Code</Label>
              <p className="text-sm font-mono font-semibold text-muted-foreground mt-1">
                {promoCode.code}
              </p>
            </div>

            <div>
              <Label className="pb-2">Discount Type</Label>
              <div className="mt-1">
                <Badge variant="outline">
                  {promoCode.discount.type === DiscountType.PERCENTAGE
                    ? "Percentage"
                    : "Fixed"}
                </Badge>
              </div>
            </div>

            <div>
              <Label className="pb-2">Discount Value</Label>
              <p className="text-sm font-semibold text-muted-foreground mt-1">
                {formatDiscount(promoCode)}
              </p>
            </div>

            <div>
              <Label className="pb-2">Enabled For</Label>
              <div className="mt-1">{getEnabledForBadge(promoCode.enabledFor)}</div>
            </div>

            <div>
              <Label className="pb-2">Status</Label>
              <div className="mt-1">
                {isExpired(promoCode) ? (
                  <Badge variant="destructive">Expired</Badge>
                ) : (
                  <Badge variant="default">Active</Badge>
                )}
              </div>
            </div>

            {promoCode.expirationDate && (
              <div className="col-span-1 sm:col-span-2">
                <Label className="pb-2">Expiration Date</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {new Date(promoCode.expirationDate).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <Label className="pb-2">Created At</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(promoCode.createdAt).toLocaleString()}
              </p>
            </div>
            <div>
              <Label className="pb-2">Last Updated</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(promoCode.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
